import difflib from './difflib.js';
// JavaScript port of RETAS aligner - Recursive Text Alignment Scheme
// Core alignment functionality only

// Hardcoded configuration (from Python defaults)
const CONFIG = {
    minGap: 0,
    minMatchLength: 1,
};

class Word {
    constructor(start, end, word, index = 0, mappedPlainTextIndex = null, mappedWhisperIndex = null, mappedBy = null) {
        this.start = start;
        this.end = end;
        this.word = word;
        this.index = index;
        this.mappedPlainTextIndex = mappedPlainTextIndex;
        this.mappedWhisperIndex = mappedWhisperIndex;
        this.mappedBy = mappedBy;
    }

    toString() {
        return this.word;
    }

    toStrNormalized() {
        return this.word.toLowerCase();
    }
}

function recursiveAlign(whisperWords, plainTextWords, memo = new Map()) {
    // Create a key for memoization
    const key = JSON.stringify([
        whisperWords.map(w => w.index),
        plainTextWords.map(w => w.index)
    ]);

    if (memo.has(key)) {
        return memo.get(key);
    }

    // Base cases
    if (!whisperWords.length || !plainTextWords.length) {
        const result = [[], []];
        memo.set(key, result);
        return result;
    }

    const words1 = whisperWords.map(word => word.toStrNormalized());
    const words2 = plainTextWords.map(word => word.toStrNormalized());

    // Find all common subsequences of reasonable length
    let bestScore = -1;
    let bestWhisperAligned = [];
    let bestPlainTextAligned = [];

    // Try different match lengths and positions
    const seqMatcher = new difflib.SequenceMatcher(null, words1, words2);

    // Get all matching blocks
    const matches = seqMatcher.getMatchingBlocks();

    // Filter out trivial matches and sort by size (descending)
    const significantMatches = matches
        .filter(([a, b, size]) => size >= CONFIG.minMatchLength)
        .sort((a, b) => b[2] - a[2]);

    // If no significant matches, return empty
    if (significantMatches.length === 0) {
        const result = [[], []];
        memo.set(key, result);
        return result;
    }

    // Evaluate top N matches (to avoid exponential complexity)
    const maxCandidates = Math.min(2, significantMatches.length);

    for (let matchIdx = 0; matchIdx < maxCandidates; matchIdx++) {
        const [a, b, size] = significantMatches[matchIdx];

        // Skip if this match doesn't meet minimum requirements
        if (size < CONFIG.minMatchLength) {
            continue;
        }

        // Create copies of the words for this candidate
        const candidateWhisper = whisperWords.map(w =>
            new Word(w.start, w.end, w.word, w.index)
        );
        const candidatePlainText = plainTextWords.map(w =>
            new Word(w.start, w.end, w.word, w.index)
        );

        // Mark the matched words
        for (let i = 0; i < size; i++) {
            candidatePlainText[b + i].mappedWhisperIndex = candidateWhisper[a + i].index;
            candidatePlainText[b + i].mappedBy = 'lcs';

            candidateWhisper[a + i].mappedPlainTextIndex = candidatePlainText[b + i].index;
            candidateWhisper[a + i].mappedBy = 'lcs';
        }

        // Recursively process left side
        let whisperLeft = [], plainTextLeft = [];
        if (a >= CONFIG.minGap) {
            [whisperLeft, plainTextLeft] = recursiveAlign(
                whisperWords.slice(0, a),
                plainTextWords.slice(0, b),
                memo
            );
        }

        // Recursively process right side
        let whisperRight = [], plainTextRight = [];
        if (whisperWords.length - (a + size) >= CONFIG.minGap) {
            [whisperRight, plainTextRight] = recursiveAlign(
                whisperWords.slice(a + size),
                plainTextWords.slice(b + size),
                memo
            );
        }

        // Current match
        const whisperMatch = candidateWhisper.slice(a, a + size);
        const plainTextMatch = candidatePlainText.slice(b, b + size);

        // Combine all parts
        const candidateWhisperAligned = [...whisperLeft, ...whisperMatch, ...whisperRight];
        const candidatePlainTextAligned = [...plainTextLeft, ...plainTextMatch, ...plainTextRight];

        // Calculate score (total number of aligned words)
        const score = candidateWhisperAligned.filter(w => w.mappedBy === 'lcs').length;

        // Update best if this is better
        if (score > bestScore) {
            bestScore = score;
            bestWhisperAligned = candidateWhisperAligned;
            bestPlainTextAligned = candidatePlainTextAligned;
        }
    }

    // Memoize and return the best result
    const result = [bestWhisperAligned, bestPlainTextAligned];
    memo.set(key, result);
    return result;
}

function plainTextToListOfWords(text) {
    const words = text.trim().split(/\s+/);
    const wordList = [];

    let wordStart = 0;
    let wordEnd = 1;

    words.forEach((word, index) => {
        wordList.push(new Word(wordStart, wordEnd, word, index));
        wordStart += 2;
        wordEnd += 2;
    });

    return wordList;
}

function overwriteWordsInCollection(smallCollection, bigCollection) {
    const bigCollectionDict = {};
    bigCollection.forEach(word => {
        bigCollectionDict[word.index] = word;
    });

    smallCollection.forEach(word => {
        bigCollectionDict[word.index] = word;
    });

    return Object.values(bigCollectionDict);
}

function addWhisperTimestampsToPlainText(plainTextWords, whisperWords) {
    const whisperWordDict = {};
    whisperWords.forEach(w => {
        whisperWordDict[w.index] = w;
    });

    plainTextWords.forEach(word => {
        if (word.mappedWhisperIndex !== null) {
            const whisperWord = whisperWordDict[word.mappedWhisperIndex];
            if (whisperWord) {
                word.start = whisperWord.start;
                word.end = whisperWord.end;
            }
        } else {
            word.start = null;
            word.end = null;
        }
    });

    return plainTextWords;
}

function levenshteinOverwriteWordsInCollection(wordsToBeOverwritten, wordsToOverwriteWith) {
    const stringNonAlignedSegmentWhisper = wordsToBeOverwritten.map(word => word.word).join(' ');
    const stringNonAlignedSegmentPlainText = wordsToOverwriteWith.map(word => word.word).join(' ');

    const wordStrings = wordsToOverwriteWith.map(word => word.word);

    if (wordsToOverwriteWith.length === wordsToBeOverwritten.length) {
        for (let i = 0; i < wordsToBeOverwritten.length; i++) {
            wordsToBeOverwritten[i].word = wordStrings[i];
            wordsToBeOverwritten[i].mappedBy = 'levenshtein';
        }
    } else if (wordsToOverwriteWith.length < wordsToBeOverwritten.length) {
        // If smaller, fill the beginning of the list, remaining words will be empty
        for (let i = 0; i < wordsToOverwriteWith.length; i++) {
            wordsToBeOverwritten[i].word = wordStrings[i];
            wordsToBeOverwritten[i].mappedBy = 'levenshtein';
        }
        for (let i = wordsToOverwriteWith.length; i < wordsToBeOverwritten.length; i++) {
            wordsToBeOverwritten[i].word = '';
        }
    } else if (wordsToOverwriteWith.length > wordsToBeOverwritten.length) {
        // If bigger, last word gets all the text of excess words
        for (let i = 0; i < wordsToBeOverwritten.length; i++) {
            wordsToBeOverwritten[i].word = wordStrings[i];
            wordsToBeOverwritten[i].mappedBy = 'levenshtein';
        }
        for (let i = wordsToBeOverwritten.length; i < wordsToOverwriteWith.length; i++) {
            wordsToBeOverwritten[wordsToBeOverwritten.length - 1].word += ' ' + wordStrings[i];
        }
    }


    return wordsToBeOverwritten;
}

function getNonAlignedSegmentsInCollection(whisperWordsMerged, plainTextWordsMerged) {
    const nonAlignedSegmentsWhisper = [];
    const nonAlignedSegmentsPlainText = [];

    let whisperStartIndex = null;
    let whisperEndIndex = null;
    let plainTextStartIndex = null;
    let plainTextEndIndex = null;

    let i = 0;
    while (i < whisperWordsMerged.length) {
        const word = whisperWordsMerged[i];
        if (word.mappedBy === 'lcs' && word.mappedPlainTextIndex !== null) {
            whisperStartIndex = word.index;
            plainTextStartIndex = word.mappedPlainTextIndex;
            i++;
        } else {
            // We're in a non-aligned segment
            for (let j = i; j < whisperWordsMerged.length; j++) {
                const wordJ = whisperWordsMerged[j];
                if (wordJ.mappedBy === 'lcs' && wordJ.mappedPlainTextIndex !== null) {
                    whisperEndIndex = wordJ.index;
                    plainTextEndIndex = wordJ.mappedPlainTextIndex;

                    const currentSegmentWhisper = whisperWordsMerged.slice(whisperStartIndex, whisperEndIndex);
                    const currentSegmentPlainText = plainTextWordsMerged.slice(plainTextStartIndex, plainTextEndIndex);

                    nonAlignedSegmentsWhisper.push(currentSegmentWhisper);
                    nonAlignedSegmentsPlainText.push(currentSegmentPlainText);

                    i = j;
                    break;
                } else {
                    i++;
                }
            }
        }
    }

    return [nonAlignedSegmentsWhisper, nonAlignedSegmentsPlainText];
}

function sanitizeWords(words, videoDuration = null) {
    let i = 0;

    while (i < words.length) {
        // If the current word has a start and end time, move on to the next word
        if (words[i].start != null && words[i].end != null) {
            i++;
            continue;
        }

        // Find the next word with a start time
        let nextWordWithTime = null;
        let j = i + 1;
        for (j = i + 1; j < words.length; j++) {
            if (words[j].start != null && words[j].end != null) {
                nextWordWithTime = words[j];
                break;
            }
        }

        // If there is no next word with a start time, distribute remaining time evenly
        if (nextWordWithTime === null) {
            // Calculate remaining time from last known timestamp to video end
            const lastEndTime = (i > 0 && words[i - 1].end != null) ? words[i - 1].end : 0;
            const remainingTime = videoDuration - lastEndTime;
            const wordsToFill = words.length - i;
            const timePerWord = remainingTime / wordsToFill;

            for (let k = i; k < words.length; k++) {
                const prevEnd = (k > 0 && words[k - 1].end != null) ? words[k - 1].end : 0;
                words[k].start = Math.round((prevEnd) * 1000) / 1000;
                words[k].end = Math.round((words[k].start + timePerWord) * 1000) / 1000;
            }
            break;
        }

        // Calculate the start and end times for the words without them
        let timeGap;
        if (i === 0) { // If the first word has no start time, use the start time of the next word
            timeGap = nextWordWithTime.start;
        } else {
            const prevEnd = words[i - 1].end != null ? words[i - 1].end : 0;
            timeGap = nextWordWithTime.start - prevEnd;
        }

        const timePerWord = timeGap / (j - i + 1);

        for (let k = i; k < j; k++) {
            const prevEnd = (k > 0 && words[k - 1].end != null) ? words[k - 1].end : 0;
            words[k].start = Math.round(prevEnd * 1000) / 1000;
            words[k].end = Math.round((words[k].start + timePerWord) * 1000) / 1000;
        }

        // Move on to the next word
        i = j;
    }

    return words;
}

// Main alignment function
function alignGetPlainTextWords(jsonWhisper, plainText, videoDuration = null) {
    const whisperWords = [];
    const wordSegments = jsonWhisper.word_segments || jsonWhisper;

    wordSegments.forEach((entry, index) => {
        whisperWords.push(new Word(entry.start, entry.end, entry.word, index));
    });

    const plainTextWords = plainTextToListOfWords(plainText);

    if (plainTextWords.length === 0 || whisperWords.length === 0) {
        return { word_segments: plainTextWords.map(w => ({ word: w.word, start: w.start, end: w.end })) };
    }

    const [whisperAlignedWords, plainTextAlignedWords] = recursiveAlign(whisperWords, plainTextWords);

    const plainTextWordsMerged = overwriteWordsInCollection(plainTextAlignedWords, plainTextWords);
    const whisperWordsMerged = overwriteWordsInCollection(whisperAlignedWords, whisperWords);

    const plainTextWordsWithTimestamps = addWhisperTimestampsToPlainText(plainTextWordsMerged, whisperWordsMerged);


    const [nonAlignedSegmentsWhisper, nonAlignedSegmentsPlainText] = getNonAlignedSegmentsInCollection(
        whisperWordsMerged,
        plainTextWordsMerged
    );

    for (let i = 0; i < nonAlignedSegmentsWhisper.length; i++) {
        levenshteinOverwriteWordsInCollection(
            nonAlignedSegmentsWhisper[i],
            nonAlignedSegmentsPlainText[i]
        );
    }


    // Sanitize words to fill in missing timestamps
    const sanitizedWords = sanitizeWords(plainTextWordsWithTimestamps, videoDuration);

    // Return only the essential fields
    return {
        word_segments: sanitizedWords.map(word => ({
            word: word.word,
            start: word.start,
            end: word.end
        }))
    };
}

// Export for ES6 modules
export { alignGetPlainTextWords, CONFIG, Word };

// Also provide default export
export default alignGetPlainTextWords;
